import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
    Box,
    Button,
    IconButton,
    Modal,
    Tooltip,
    Typography
} from '@mui/material';
import { ReactTable } from '@components';
import { MRT_ToggleGlobalFilterButton } from 'material-react-table';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CreateProfile from './CreateProfile';
import { useDispatch, useSelector } from 'react-redux';
import { apiDeletePegnordProfile, apiGetPegnordSetPriceProfile } from '@store';
import { showToast } from '@utils';
import Buy from './Buy';

const Profiles = () => {
    const dispatch = useDispatch();
    const [selectedProfile, setSelectedProfile] = useState();
    const { user } = useSelector((state) => state.user);

    const {
        pegnordEur,
        chf,
        setPegnordPriceProfile,
        isGetSetPriceProfileLoading
    } = useSelector((state) => state.pegnord);

    const { disableTrading } = useSelector((state) => state.the);
    const disableTradingStatus = disableTrading?.disable;
    const canBuy =
        disableTradingStatus === false && user?.HideBuyButton === false;

    // Load Pegnord price profiles on component mount
    useEffect(() => {
        dispatch(apiGetPegnordSetPriceProfile('PEG'));
    }, [dispatch]);

    // open modal
    const [openModal, setOpenModal] = useState(false);
    const handleOpenModal = (singlePriceProfile) => {
        setOpenModal(true);
        setSelectedProfile(singlePriceProfile);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // add profile modal
    const [openAddProfileModal, setOpenAddProfileModal] = useState(false);
    const handleOpenAddProfileModal = () => {
        setOpenAddProfileModal(true);
    };
    const handleCloseAddProfileModal = () => {
        setOpenAddProfileModal(false);
    };

    const columns = useMemo(
        () => [
            {
                header: 'Profile Name',
                id: 'name',
                accessorFn: ({ name }) => name || '',
                Cell: ({ renderedCellValue }) => (
                    <Typography sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        {renderedCellValue}
                    </Typography>
                ),
                size: 50
            },
            {
                header: 'Created At',
                id: 'created_at',
                accessorFn: ({ created_at }) => created_at || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: 'Location',
                id: 'location',
                accessorFn: () => 'PEG',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            },
            {
                header: 'Currency',
                id: 'currency',
                accessorFn: ({ currency }) => currency || '',
                Cell: ({ renderedCellValue }) => <b>{renderedCellValue}</b>,
                size: 50
            }
        ],
        []
    );

    // ask window to delete profile
    const handleDeleteProfile = useCallback(
        async (profile) => {
            if (
                window.confirm(
                    `Are you sure you want to delete ${profile.name} profile?`
                )
            ) {
                const resp = await dispatch(
                    apiDeletePegnordProfile({ data: profile })
                );
                if (resp?.meta?.requestStatus === 'fulfilled') {
                    showToast('info', 'Profile deleted successfully');
                    dispatch(apiGetPegnordSetPriceProfile('PEG'));
                } else if (resp?.meta?.requestStatus === 'rejected') {
                    showToast(
                        'error',
                        resp.payload || 'Failed to delete profile'
                    );
                }
            }
        },
        [dispatch]
    );

    const handleRowClick = useCallback(
        (row) => {
            handleOpenModal(row?.original);
        },
        []
    );

    const actionsMenu = useMemo(
        () =>
            ({ row }) => [
                <Box
                    className="flex gap-4 items-center justify-center"
                    key="buttons"
                >
                    <Button
                        variant="outlined"
                        color="primary"
                        sx={{
                            p: 0,
                            m: 0,
                            borderColor: '1px solid #27A9FF',
                            color: '#27A9FF',
                            fontSize: '13px',
                            '&:hover': {
                                borderColor: '1px solid #27A9FF',
                                color: 'white',
                                backgroundColor: '#27A9FF'
                            }
                        }}
                        disabled={!canBuy}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenModal(row?.original);
                        }}
                    >
                        Buy
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        sx={{
                            p: 0,
                            m: 0,
                            borderColor: 'brown',
                            color: 'brown',
                            fontSize: '13px',
                            '&:hover': {
                                borderColor: 'brown',
                                color: 'white',
                                backgroundColor: 'brown'
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProfile(row?.original);
                        }}
                    >
                        Delete
                    </Button>
                </Box>
            ],
        [canBuy, handleDeleteProfile]
    );

    return (
        <Box>
            <Modal open={openModal} onClose={handleCloseModal}>
                <Buy
                    handleClose={handleCloseModal}
                    open={openModal}
                    singlePriceProfile={selectedProfile}
                    eur={pegnordEur}
                    chf={chf}
                    disabled={!canBuy}
                />
            </Modal>
            <Modal
                open={openAddProfileModal}
                onClose={handleCloseAddProfileModal}
            >
                <CreateProfile
                    handleClose={handleCloseAddProfileModal}
                    open={openAddProfileModal}
                    eur={pegnordEur}
                    chf={chf}
                    user={user}
                    market="PEG"
                />
            </Modal>
            <ReactTable
                renderToolbarInternalActions={({ table }) => (
                    <div onMouseDown={(e) => e.stopPropagation()}>
                        <MRT_ToggleGlobalFilterButton table={table} />
                        <Tooltip title="Create Profile">
                            <IconButton onClick={handleOpenAddProfileModal}>
                                <AddBoxOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
                headerText={'PEG Profiles'}
                columns={columns}
                data={setPegnordPriceProfile || []}
                actions={actionsMenu}
                isLoading={isGetSetPriceProfileLoading}
                muiTableBodyRowProps={({ row }) => ({
                    onClick: () => handleRowClick(row),
                    sx: { cursor: canBuy ? 'pointer' : 'default' }
                })}
            />
        </Box>
    );
};

export default Profiles;
